import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Workflow, Sparkles, ChevronLeft, ChevronRight, CalendarDays, Plus, Trash2, X,
  Stethoscope, Users, Wrench, Clock, MapPin, AlertTriangle, CheckCircle2, Search, Activity, UserCog,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import ProviderFinder from "@/components/fleet/ProviderFinder";
import { api, formatApiError } from "@/lib/api";

const ROLE_META = {
  physician: { label: "Physician", icon: Stethoscope, color: "#0B7E8C" },
  nurse: { label: "Nurse", icon: Users, color: "#8B5CF6" },
  technician: { label: "Technician", icon: Wrench, color: "#F59E0B" },
  physiotherapist: { label: "Physiotherapist", icon: Activity, color: "#0EA5E9" },
};
const PRIO = { critical: "#DC2626", urgent: "#F59E0B", routine: "#64748B" };

const todayRiyadh = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const shiftDay = (iso, d) => { const x = new Date(`${iso}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + d); return x.toISOString().slice(0, 10); };
const fmtDayLabel = (iso) => { try { const x = new Date(`${iso}T12:00:00Z`); const l = x.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); return iso === todayRiyadh() ? `Today · ${l}` : l; } catch { return iso; } };
const timeOf = (iso) => (iso ? String(iso).slice(11, 16) : "");

export default function Orchestrator() {
  const [day, setDay] = useState(todayRiyadh);
  const [orders, setOrders] = useState([]);
  const [plan, setPlan] = useState(null);
  const [edit, setEdit] = useState({});          // patient_id -> { time, team: {roleIdx: clinician_id} }
  const [selected, setSelected] = useState({});   // patient_id -> bool
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [availByRow, setAvailByRow] = useState({});  // patient_id -> {loading, all_available, results}
  const [finder, setFinder] = useState({ open: false });

  const runCheck = useCallback(async (rowKey, teamIds, time, duration) => {
    const ids = (teamIds || []).filter(Boolean);
    if (ids.length === 0 || !time) { setAvailByRow((s) => ({ ...s, [rowKey]: null })); return; }
    setAvailByRow((s) => ({ ...s, [rowKey]: { loading: true } }));
    try {
      const r = await api.get("/availability/check", { params: { user_ids: ids.join(","), date: day, time, duration: duration || 30 } });
      setAvailByRow((s) => ({ ...s, [rowKey]: { loading: false, ...r.data } }));
    } catch { setAvailByRow((s) => ({ ...s, [rowKey]: null })); }
  }, [day]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/service-orders", { params: { status: "pending" } }); setOrders(r.data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  async function orchestrate(manual = false) {
    setPlanning(true); setPlan(null);
    try {
      const r = await api.post("/fleet/orchestrate", { day });
      setPlan({ ...r.data, manual });
      const e = {}, sel = {};
      (r.data.plan || []).forEach((row) => {
        const team = {};
        // Manual mode: leave provider slots empty for deliberate selection.
        row.team.forEach((t, i) => { team[i] = manual ? "" : (t.chosen?.clinician_id || ""); });
        e[row.patient_id] = { time: timeOf(row.proposed_time) || "08:00", team };
        sel[row.patient_id] = manual ? false : row.feasible;
      });
      setEdit(e); setSelected(sel);
      // Verify each proposed row against live availability (auto mode only — manual starts empty).
      if (!manual) {
        (r.data.plan || []).forEach((row) => {
          const ids = row.team.map((t, i) => e[row.patient_id].team[i]).filter(Boolean);
          runCheck(row.patient_id, ids, e[row.patient_id].time, row.est_duration_min);
        });
      } else {
        setAvailByRow({});
      }
      if ((r.data.plan || []).length === 0) toast.info("No pending orders to orchestrate for this day.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPlanning(false); }
  }

  function openFinder(r, t, i) {
    const m = ROLE_META[t.role] || ROLE_META.nurse;
    setFinder({
      open: true,
      patientId: r.patient_id,
      roleIndex: i,
      duration: r.est_duration_min,
      title: `Find ${m.label} for ${r.patient_name}`,
      request: {
        day,
        role: t.role,
        skills: t.requirement?.skills || [],
        medical_specialty: t.requirement?.medical_specialty || null,
        specialty_type: t.requirement?.specialty_type || null,
        patient_id: r.patient_id,
        lat: r.patient_location?.latitude ?? null,
        lng: r.patient_location?.longitude ?? null,
        duration: r.est_duration_min,
      },
    });
  }

  function onPickProvider(clinicianId, slotTime) {
    const pid = finder.patientId, i = finder.roleIndex;
    setEdit((s) => {
      const cur = s[pid] || { time: "08:00", team: {} };
      const team = { ...cur.team, [i]: clinicianId };
      const time = slotTime || cur.time;
      const teamIds = Object.keys(team).sort((a, b) => a - b).map((k) => team[k]);
      runCheck(pid, teamIds, time, finder.duration);
      return { ...s, [pid]: { ...cur, team, time } };
    });
    toast.success(slotTime ? `Assigned at ${slotTime}` : "Provider selected");
    setFinder({ open: false });
  }

  function optionsFor(t) {
    const opts = [];
    if (t.chosen) opts.push(t.chosen);
    (t.alternatives || []).forEach((a) => { if (!opts.find((o) => o.clinician_id === a.clinician_id)) opts.push(a); });
    return opts;
  }

  async function commit() {
    const rows = (plan?.plan || []).filter((r) => selected[r.patient_id]);
    const assignments = [];
    for (const r of rows) {
      const e = edit[r.patient_id];
      const teamIds = r.team.map((t, i) => e.team[i]).filter(Boolean);
      if (teamIds.length !== r.team.length) { toast.error(`${r.patient_name}: assign every role first`); return; }
      const av = availByRow[r.patient_id];
      if (av && av.all_available === false && !e.override) {
        toast.error(`${r.patient_name}: team not available at ${e.time}. Adjust the time or tick Override.`); return;
      }
      assignments.push({ order_ids: r.order_ids, scheduled_at: `${day}T${e.time}:00+03:00`, team_ids: teamIds, override: !!e.override });
    }
    if (assignments.length === 0) { toast.error("Select at least one row to commit."); return; }
    setCommitting(true);
    try {
      const res = await api.post("/fleet/orchestrate/commit", { assignments });
      toast.success(`Committed ${res.data.committed} assignment(s)`);
      setPlan(null); setEdit({}); setSelected({}); loadOrders();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  }

  async function delOrder(id) {
    try { await api.delete(`/service-orders/${id}`); loadOrders(); if (plan) setPlan({ ...plan, plan: plan.plan.filter((r) => r.order_id !== id) }); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  const selCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  return (
    <div data-testid="orchestrator-page">
      <PageHeader
        title="Service Assignment"
        subtitle="Turn pending patient service orders into an optimized, reviewable assignment plan — best-fit teams matched by skill, availability and route."
        actions={
          <Button onClick={() => setAddOpen(true)} variant="outline" className="border-[#E5E7EB]" data-testid="orch-add-order">
            <Plus className="w-4 h-4 mr-1.5" /> Add Service Order
          </Button>
        }
      />

      {/* Control bar */}
      <div className="hah-card p-3 mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center rounded-lg border border-[#E5E7EB] bg-white overflow-hidden" data-testid="orch-day-nav">
          <button onClick={() => setDay((d) => shiftDay(d, -1))} className="px-2 py-2 hover:bg-[#F8FAFC]" data-testid="orch-prev-day"><ChevronLeft className="w-4 h-4" /></button>
          <label className="relative flex items-center gap-2 px-3 py-2 border-x border-[#E5E7EB] cursor-pointer">
            <CalendarDays className="w-4 h-4 text-[#0B7E8C]" />
            <span className="text-[13px] font-medium text-[#1F2937] whitespace-nowrap" data-testid="orch-day-label">{fmtDayLabel(day)}</span>
            <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" data-testid="orch-day-input" />
          </label>
          <button onClick={() => setDay((d) => shiftDay(d, 1))} className="px-2 py-2 hover:bg-[#F8FAFC]" data-testid="orch-next-day"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <span className="text-[13px] text-[#6B7280]"><strong className="text-[#1F2937]">{orders.length}</strong> pending order(s)</span>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => orchestrate(true)} disabled={planning || orders.length === 0} variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#0B7E8C0A]" data-testid="orch-manual">
            <UserCog className="w-4 h-4 mr-1.5" /> Assign Manually
          </Button>
          <Button onClick={() => orchestrate(false)} disabled={planning || orders.length === 0} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="orch-run">
            <Sparkles className="w-4 h-4 mr-1.5" /> {planning ? "Assigning…" : "Auto Assign"}
          </Button>
          {plan && (
            <Button onClick={commit} disabled={committing || selCount === 0} className="bg-[#065F46] hover:bg-[#047857] text-white" data-testid="orch-commit">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> {committing ? "Committing…" : `Commit ${selCount}`}
            </Button>
          )}
        </div>
      </div>

      {/* Pending worklist (before planning) */}
      {!plan && (
        loading ? (
          <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="hah-card p-10 text-center" data-testid="orch-empty">
            <Workflow className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No pending service orders. Add orders, then click Auto Assign.</p>
          </div>
        ) : (
          <div className="hah-card p-4">
            <div className="text-[13px] font-semibold text-[#1F2937] mb-2">Pending Service Orders</div>
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#F1F5F9]" data-testid={`orch-order-${o.id}`}>
                  <span className="w-1.5 h-8 rounded-full" style={{ background: PRIO[o.priority] || PRIO.routine }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-[#1F2937] truncate">{o.patient_name} <span className="text-[#94A3B8] font-normal">· {o.mrn}</span></div>
                    <div className="text-[12px] text-[#6B7280]">{o.service_name}{o.city ? ` · ${o.city}` : ""} · {o.est_duration_min || "?"} min</div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(o.required_roles || []).map((r, i) => { const m = ROLE_META[r.role] || ROLE_META.nurse; const I = m.icon; return (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${m.color}14`, color: m.color }}><I className="w-3 h-3" />{r.count > 1 ? `${r.count}×` : ""}{m.label}</span>
                    ); })}
                  </div>
                  <button onClick={() => delOrder(o.id)} className="text-[#EF4444] p-1 shrink-0" data-testid={`orch-del-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Proposed plan */}
      {plan && (
        <div className="space-y-3" data-testid="orch-plan">
          <div className="flex items-center gap-3 text-[13px] text-[#6B7280]">
            <span>{plan.manual ? "Manual assignment for" : "Plan for"} <strong className="text-[#1F2937]">{fmtDayLabel(day)}</strong></span>
            {plan.manual ? (
              <span className="text-[#0B7E8C] inline-flex items-center gap-1"><UserCog className="w-3.5 h-3.5" /> Pick providers for each role, then commit</span>
            ) : (
              <>
                <span className="text-[#065F46]">{plan.summary.feasible} feasible</span>
                {plan.summary.visits - plan.summary.feasible > 0 && <span className="text-[#B91C1C]">{plan.summary.visits - plan.summary.feasible} need attention</span>}
              </>
            )}
            <button onClick={() => setPlan(null)} className="ml-auto text-[#0B7E8C] hover:underline">Back to worklist</button>
          </div>
          {plan.plan.map((r) => {
            const e = edit[r.patient_id] || { time: "08:00", team: {} };
            const svcLabel = (r.services || []).map((s) => s.service_name || s.service_code).join(" + ");
            // A row is committable once every required role has a provider chosen.
            const rowReady = r.team.length > 0 && r.team.every((_, i) => !!e.team[i]);
            return (
              <div key={r.patient_id} className={`hah-card p-4 ${rowReady || r.feasible ? "" : "border-[#FCA5A5]"}`} data-testid={`orch-plan-row-${r.patient_id}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={!!selected[r.patient_id]} disabled={!rowReady}
                         onChange={(ev) => setSelected((s) => ({ ...s, [r.patient_id]: ev.target.checked }))}
                         className="mt-1.5" data-testid={`orch-select-${r.patient_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-[#1F2937]">{r.patient_name} <span className="text-[12px] text-[#94A3B8] font-normal">· {r.mrn}</span></div>
                        <div className="text-[12px] text-[#6B7280]">{svcLabel} · {r.est_duration_min} min{r.city ? ` · ${r.city}` : ""}</div>
                        {(r.services || []).length > 1 && (
                          <div className="flex flex-wrap gap-1 mt-1" data-testid={`orch-services-${r.patient_id}`}>
                            {r.services.map((s, si) => (
                              <span key={si} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#0B7E8C14] text-[#0B7E8C]">
                                {s.service_name || s.service_code} · {s.est_duration_min}m
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-medium capitalize" style={{ background: `${PRIO[r.priority]}1A`, color: PRIO[r.priority] }}>{r.priority}</span>
                        <label className="inline-flex items-center gap-1.5 text-[12px] text-[#374151] border border-[#E5E7EB] rounded-lg px-2 py-1">
                          <Clock className="w-3.5 h-3.5 text-[#0B7E8C]" />
                          <input type="time" value={e.time} onChange={(ev) => { const nt = ev.target.value; setEdit((s) => ({ ...s, [r.patient_id]: { ...e, time: nt } })); runCheck(r.patient_id, r.team.map((t, i) => e.team[i]), nt, r.est_duration_min); }}
                                 className="bg-transparent outline-none text-[12px]" data-testid={`orch-time-${r.patient_id}`} />
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                      {r.team.map((t, i) => {
                        const m = ROLE_META[t.role] || ROLE_META.nurse; const I = m.icon;
                        const opts = optionsFor(t);
                        const val = e.team[i] || "";
                        return (
                          <div key={i} className="rounded-lg border border-[#F1F5F9] p-2.5" data-testid={`orch-team-${r.patient_id}-${i}`}>
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5" style={{ color: m.color }}>
                              <I className="w-3.5 h-3.5" /> {m.label}
                              {(t.requirement?.skills || []).length > 0 && <span className="text-[#94A3B8] font-normal">· {t.requirement.skills.join(", ")}</span>}
                              <button onClick={() => openFinder(r, t, i)} data-testid={`orch-find-${r.patient_id}-${i}`}
                                className="ml-auto text-[10px] text-[#0B7E8C] font-medium hover:underline inline-flex items-center gap-0.5">
                                <Search className="w-3 h-3" /> Find
                              </button>
                            </div>
                            {opts.length === 0 ? (
                              <div className="text-[12px] text-[#B91C1C] inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> No candidate available</div>
                            ) : (
                              <Select value={val} onValueChange={(v) => { const nt = { ...e.team, [i]: v }; setEdit((s) => ({ ...s, [r.patient_id]: { ...e, team: nt } })); runCheck(r.patient_id, r.team.map((_, j) => nt[j]), e.time, r.est_duration_min); }}>
                                <SelectTrigger className="h-8 text-[13px]" data-testid={`orch-provider-${r.patient_id}-${i}`}><SelectValue placeholder="Select provider" /></SelectTrigger>
                                <SelectContent>
                                  {opts.map((o) => (
                                    <SelectItem key={o.clinician_id} value={o.clinician_id}>
                                      {o.clinician_name} · {o.score}{o.distance_km != null ? ` · ${o.distance_km}km` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {(() => {
                      const av = availByRow[r.patient_id];
                      if (!av) return null;
                      if (av.loading) return <div className="text-[11px] text-[#6B7280] mt-2">Checking availability…</div>;
                      const REASON = { off: "off / outside working hours", conflict: "already booked", no_availability: "no availability set" };
                      const winStr = (av.results || []).map((x) => x.windows && x.windows.length ? `${x.name}: ${x.windows.join(", ")}` : `${x.name}: no windows`).join("  ·  ");
                      if (av.all_available) return (
                        <div className="mt-2" data-testid={`orch-avail-ok-${r.patient_id}`}>
                          <div className="text-[11px] text-[#065F46] inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Team available at {e.time}
                          </div>
                          {winStr && <div className="text-[10px] text-[#94A3B8] mt-0.5">Open: {winStr}</div>}
                        </div>
                      );
                      const bad = (av.results || []).filter((x) => !x.available);
                      return (
                        <div className="mt-2" data-testid={`orch-avail-bad-${r.patient_id}`}>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-[11px] text-[#B91C1C] inline-flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5" /> {bad.map((x) => `${x.name} (${REASON[x.reason] || x.reason})`).join(", ")}
                            </span>
                            <label className="text-[11px] text-[#B45309] inline-flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={!!e.override} onChange={(ev) => setEdit((s) => ({ ...s, [r.patient_id]: { ...e, override: ev.target.checked } }))} data-testid={`orch-override-${r.patient_id}`} />
                              Override & assign anyway
                            </label>
                          </div>
                          {winStr && <div className="text-[10px] text-[#94A3B8] mt-0.5">Open windows — {winStr}</div>}
                        </div>
                      );
                    })()}
                    {r.warnings?.length > 0 && (
                      <div className="text-[11px] text-[#B45309] mt-2 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {r.warnings.join(" · ")}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddOrderSheet open={addOpen} onOpenChange={setAddOpen} onAdded={loadOrders} />
      <ProviderFinder open={finder.open} onOpenChange={(o) => setFinder((f) => ({ ...f, open: o }))}
        title={finder.title} request={finder.open ? finder.request : null} onPick={onPickProvider} />
    </div>
  );
}

function AddOrderSheet({ open, onOpenChange, onAdded }) {
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceCodes, setServiceCodes] = useState([]);
  const [priority, setPriority] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) api.get("/service-catalog", { params: { active: true } }).then((r) => setServices(r.data.items || [])).catch(() => {}); }, [open]);
  useEffect(() => { if (!open) { setPatient(null); setQ(""); setServiceCodes([]); setPriority(""); } }, [open]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setPatients([]); return; }
    const t = setTimeout(() => { api.get("/patients", { params: { q, page_size: 8 } }).then((r) => setPatients(r.data.items || [])).catch(() => {}); }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  function toggleService(code) {
    setServiceCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  }

  async function save() {
    if (!patient || serviceCodes.length === 0) { toast.error("Pick a patient and at least one service"); return; }
    setSaving(true);
    try {
      for (const code of serviceCodes) {
        await api.post(`/patients/${patient.id}/service-orders`, { service_code: code, priority: priority || null });
      }
      toast.success(`${serviceCodes.length} service order(s) added`);
      setPatient(null); setQ(""); setServiceCodes([]); setPriority(""); onOpenChange(false); onAdded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md" data-testid="orch-add-sheet">
        <SheetHeader><SheetTitle>Add Service Order</SheetTitle><SheetDescription>Attach a requested catalog service to a patient.</SheetDescription></SheetHeader>
        <div className="space-y-4 mt-5">
          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1">Patient</label>
            {patient ? (
              <div className="flex items-center justify-between border border-[#E5E7EB] rounded-lg px-3 py-2">
                <span className="text-[14px] text-[#1F2937]">{patient.first_name} {patient.last_name} <span className="text-[#94A3B8]">· {patient.mrn}</span></span>
                <button onClick={() => setPatient(null)} className="text-[#94A3B8]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border border-[#E5E7EB] rounded-lg px-3 py-2">
                  <Search className="w-4 h-4 text-[#94A3B8]" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or MRN…" className="flex-1 text-sm outline-none bg-transparent" data-testid="orch-patient-search" />
                </div>
                {patients.length > 0 && (
                  <div className="mt-1 border border-[#E5E7EB] rounded-lg divide-y max-h-52 overflow-auto">
                    {patients.map((p) => (
                      <button key={p.id} onClick={() => { setPatient(p); setPatients([]); }} className="w-full text-left px-3 py-2 hover:bg-[#F8FAFC] text-[13px]" data-testid={`orch-patient-opt-${p.id}`}>
                        {p.first_name} {p.last_name} <span className="text-[#94A3B8]">· {p.mrn}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1">Services <span className="text-[#94A3B8] font-normal">· select one or more (combined into one visit)</span></label>
            <div className="border border-[#E5E7EB] rounded-lg divide-y max-h-60 overflow-auto" data-testid="orch-service-list">
              {services.map((s) => {
                const checked = serviceCodes.includes(s.code);
                return (
                  <label key={s.code} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer" data-testid={`orch-service-opt-${s.code}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleService(s.code)} className="rounded" />
                    <span className="flex-1 text-[13px] text-[#1F2937]">{s.name}</span>
                    <span className="text-[11px] text-[#94A3B8]">{s.est_duration_min || "?"} min</span>
                  </label>
                );
              })}
            </div>
            {serviceCodes.length > 0 && <div className="mt-1 text-[11px] text-[#0B7E8C]">{serviceCodes.length} selected</div>}
          </div>
          <div>
            <label className="block text-[12px] font-medium text-[#374151] mb-1">Priority (optional)</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="orch-priority-select"><SelectValue placeholder="Use service default" /></SelectTrigger>
              <SelectContent>{["routine", "urgent", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white w-full" data-testid="orch-add-save">{saving ? "Adding…" : serviceCodes.length > 1 ? `Add ${serviceCodes.length} orders` : "Add order"}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
